import type {
  RecipientType,
  EquipmentType,
  DocumentType,
  AssistanceCategory,
  AssistanceCalculation,
} from "./types";

// Recipient type labels
export const RECIPIENT_TYPE_LABELS: Record<RecipientType, string> = {
  osbb: "ОСББ",
  manager: "Управитель",
  cooperative: "Кооператив",
};

export const RECIPIENT_TYPE_DESCRIPTIONS: Record<RecipientType, string> = {
  osbb: "Об'єднання співвласників багатоквартирного будинку",
  manager: "Управитель багатоквартирного будинку (крім державної та комунальної форми власності)",
  cooperative: "Житлово-будівельний (житловий, обслуговуючий) кооператив",
};

// Equipment labels
export const EQUIPMENT_LABELS: Record<EquipmentType, string> = {
  generator_petrol: "Генератор (бензиновий)",
  generator_diesel: "Генератор (дизельний)",
  generator_gas: "Генератор (газовий)",
  inverter: "Інвертор",
  battery_controller: "Блок керування високовольтними батареями",
  batteries: "Акумулятори",
  solar_panels: "Сонячні фотоелектричні модулі (панелі)",
};

// Document labels
export const DOCUMENT_LABELS: Record<DocumentType, string> = {
  doc_emergency_decision: "Рішення комісії ТЕБ та НС",
  doc_tech_passport: "Технічний паспорт будинку",
  doc_floors_certificate: "Довідка про поверховість",
  doc_edr_extract: "Витяг з ЄДР",
  doc_management_decision: "Рішення про участь у програмі допомоги",
  doc_statute: "Копія статуту",
  doc_management_contract: "Договір управління з додатками",
  doc_local_authority_letter: "Лист органу місцевого самоврядування",
};

export const DOCUMENT_DESCRIPTIONS: Record<DocumentType, string> = {
  doc_emergency_decision:
    "Рішення місцевої/регіональної/державної комісії з питань ТЕБ та НС щодо виникнення надзвичайної ситуації",
  doc_tech_passport:
    "Копія технічного паспорта будинку (сторінки з адресою та характеристикою поверховості)",
  doc_floors_certificate:
    "Довідка органу місцевого самоврядування з інформацією щодо поверховості будинку",
  doc_edr_extract: "Витяг з ЄДР (дата формування = дата подання заяви)",
  doc_management_decision:
    "Рішення органу управління-зборів співвласників",
  doc_statute: "Копія статуту організації",
  doc_management_contract:
    "Копії договору про надання послуг з управління багатоквартирним будинком з додатками",
  doc_local_authority_letter:
    "Лист органу місцевого самоврядування з підтвердженням чинності договору управління",
};

// Obligation texts (displayed as bullet points under single checkbox)
export const OBLIGATION_ITEMS = [
  "дотримуватися вимог цього Порядку, зокрема щодо цільового, ефективного та результативного використання коштів для придбання обладнання",
  "використати кошти за цільовим призначенням протягом 45 календарних днів з дати отримання допомоги",
  "надати звіт про цільове використання допомоги на придбання незалежних джерел електричної енергії для забезпечення потреб співвласників багатоквартирних будинків за формою згідно з додатком 2 протягом 10 днів після встановлення обладнання, але не пізніше ніж 20 грудня 2026 року",
  "у разі невикористання коштів або неможливості придбання обладнання повернути кошти на рахунок розпорядника не пізніше ніж 20 грудня 2026 року",
  "встановити обладнання з дотриманням вимог пожежної безпеки, санітарних норм та забезпечити його експлуатацію",
];

// Assistance calculation
export const ASSISTANCE_AMOUNTS: Record<AssistanceCategory, number> = {
  category_a: 100000,
  category_b: 200000,
  category_c: 300000,
};

export function calculateAssistance(
  floors: number,
  boilerRoomType: string | null
): AssistanceCalculation {
  // Boiler room always gives maximum category
  if (boilerRoomType) {
    const boilerLabels: Record<string, string> = {
      roof: "дахової",
      attached: "прибудованої",
      builtin: "вбудованої",
    };
    return {
      category: "category_c",
      amount: 300000,
      reason: `Наявність ${boilerLabels[boilerRoomType] || ""} котельні`,
    };
  }

  // By floors
  if (floors >= 17) {
    return {
      category: "category_c",
      amount: 300000,
      reason: `Будинок від 17 поверхів (${floors} поверхів)`,
    };
  }

  if (floors >= 7) {
    return {
      category: "category_b",
      amount: 200000,
      reason: `Будинок від 7 до 16 поверхів (${floors} поверхів)`,
    };
  }

  return {
    category: "category_a",
    amount: 100000,
    reason: `Будинок до 6 поверхів (${floors} поверхів)`,
  };
}

// Get required documents by recipient type
export function getRequiredDocuments(
  recipientType: RecipientType,
  _boilerRoomType: string | null, // kept for API compatibility
  hasTechPassport: boolean
): DocumentType[] {
  const docs: DocumentType[] = [];

  // Tech passport or certificate
  if (hasTechPassport) {
    docs.push("doc_tech_passport");
  } else {
    docs.push("doc_floors_certificate");
  }

  // EDR extract for all
  docs.push("doc_edr_extract");

  // By recipient type
  switch (recipientType) {
    case "osbb":
    case "cooperative":
      docs.push("doc_management_decision");
      docs.push("doc_statute");
      break;

    case "manager":
      docs.push("doc_management_contract");
      docs.push("doc_local_authority_letter");
      break;
  }

  return docs;
}

// Get optional documents
export function getOptionalDocuments(): DocumentType[] {
  return ["doc_emergency_decision"];
}

// Validate IBAN (Ukrainian format: UA + 27 digits with checksum validation)
// Algorithm: ISO 13616 / ISO 7064 Modulo 97-10
export function validateIBAN(iban: string): {
  valid: boolean;
  error?: string;
  bankCode?: string;  // Extracted bank code for async validation
} {
  if (!iban) return { valid: false };
  
  // Remove spaces and convert to uppercase
  const cleanIban = iban.replace(/\s/g, '').toUpperCase();
  
  // Check format: UA + 27 digits = 29 characters total
  const ibanRegex = /^UA\d{27}$/;

  if (!ibanRegex.test(cleanIban)) {
    return {
      valid: false,
      error: "Невірний формат IBAN. Має бути UA + 27 цифр",
    };
  }

  // Validate checksum using Modulo 97 algorithm
  // 1. Move first 4 characters to the end
  const rearranged = cleanIban.slice(4) + cleanIban.slice(0, 4);
  
  // 2. Replace letters with numbers (A=10, B=11, ..., Z=35)
  let numericString = '';
  for (const char of rearranged) {
    if (char >= 'A' && char <= 'Z') {
      numericString += (char.charCodeAt(0) - 55).toString(); // A=10, B=11, etc.
    } else {
      numericString += char;
    }
  }
  
  // 3. Calculate mod 97 using chunk processing (to avoid BigInt for large numbers)
  let remainder = 0;
  for (const digit of numericString) {
    remainder = (remainder * 10 + parseInt(digit, 10)) % 97;
  }
  
  // Valid IBAN has remainder of 1
  if (remainder !== 1) {
    return {
      valid: false,
      error: "Невірна контрольна сума IBAN. Перевірте правильність номера",
    };
  }

  // Extract bank code for potential async validation
  const bankCode = cleanIban.substring(4, 10);

  return { valid: true, bankCode };
}

// Async IBAN validation with NBU bank name lookup
// Primary validation is checksum (synchronous), bank lookup is informational only
import { validateBankCode } from "../nbu";

export async function validateIBANWithBankCheck(iban: string): Promise<{
  valid: boolean;
  error?: string;
  bankName?: string;
  bankFound?: boolean;
}> {
  // First, do basic validation (format + checksum) - this is the blocking validation
  const basicResult = validateIBAN(iban);
  
  if (!basicResult.valid) {
    return basicResult;
  }
  
  // Then try to lookup bank name in NBU registry (informational only, never blocks)
  if (basicResult.bankCode) {
    try {
      const bankResult = await validateBankCode(basicResult.bankCode);
      
      return {
        valid: true, // Always valid if checksum passed
        bankName: bankResult.bankName,
        bankFound: bankResult.bankFound,
      };
    } catch (error) {
      // NBU API error - don't block user, just skip bank name
      console.warn("NBU bank lookup failed:", error);
      return { valid: true, bankFound: false };
    }
  }
  
  return { valid: true, bankFound: false };
}

// Validate Email (strict format)
export function validateEmail(email: string): {
  valid: boolean;
  error?: string;
} {
  if (!email) return { valid: false };
  
  // Strict email regex:
  // - Local part: letters, numbers, dots, hyphens, underscores, plus signs only
  // - Domain: letters, numbers, hyphens, at least one dot for TLD
  // - No special characters like $, %, &, etc.
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  
  if (!emailRegex.test(email)) {
    return {
      valid: false,
      error: "Невірний формат email. Приклад: user@example.com",
    };
  }

  return { valid: true };
}

// Validate Ukrainian Phone
export function validatePhone(phone: string): {
  valid: boolean;
  error?: string;
} {
  if (!phone) return { valid: false };
  
  const cleaned = phone.replace(/\s/g, "");
  const phoneRegex = /^\+380\d{9}$/;
  
  if (!phoneRegex.test(cleaned)) {
    return {
      valid: false,
      error: "Невірний формат. Має бути +380XXXXXXXXX (12 цифр)",
    };
  }

  return { valid: true };
}

// Validate EDRPOU (8 digits with checksum validation)
// Algorithm: Weighted sum with Modulo 11
// Reference: https://github.com/alazurenko/validate-edrpou
export function validateEDRPOU(edrpou: string): {
  valid: boolean;
  error?: string;
} {
  if (!edrpou) return { valid: false };
  
  // Remove spaces
  const cleanEdrpou = edrpou.replace(/\s/g, '');
  
  const edrpouRegex = /^\d{8}$/;
  
  if (!edrpouRegex.test(cleanEdrpou)) {
    return {
      valid: false,
      error: "Код ЄДРПОУ має містити 8 цифр",
    };
  }

  // Parse digits
  const digits = cleanEdrpou.split('').map(Number);
  const numericValue = parseInt(cleanEdrpou, 10);
  const checkDigit = digits[7] ?? 0; // Last digit is the checksum
  
  // Select base weights based on the numeric value
  // For codes < 30000000 or >= 60000000: weights [1, 2, 3, 4, 5, 6, 7, 0]
  // For codes >= 30000000 and < 60000000: weights [7, 1, 2, 3, 4, 5, 6, 0]
  const baseWeights = (numericValue < 30000000 || numericValue >= 60000000)
    ? [1, 2, 3, 4, 5, 6, 7, 0]
    : [7, 1, 2, 3, 4, 5, 6, 0];
  
  // Recursive calculation function
  const calculate = (addend: number): number => {
    let sum = 0;
    for (let i = 0; i < 8; i++) {
      sum += (digits[i] ?? 0) * ((baseWeights[i] ?? 0) + addend);
    }
    const result = sum % 11;
    
    // If result > 10, recalculate with weights + 2
    if (result > 10) {
      return calculate(2);
    }
    return result;
  };
  
  const calculatedCheck = calculate(0);
  
  if (calculatedCheck !== checkDigit) {
    return {
      valid: false,
      error: "Невірна контрольна сума ЄДРПОУ. Перевірте правильність коду",
    };
  }

  return { valid: true };
}

// Validate Legal Address (min 10 characters)
export function validateLegalAddress(address: string): {
  valid: boolean;
  error?: string;
} {
  if (!address) return { valid: false };
  
  if (address.length < 10) {
    return {
      valid: false,
      error: "Юридична адреса має містити мінімум 10 символів",
    };
  }

  return { valid: true };
}

// Format helpers
export function formatMoney(amount: number): string {
  return new Intl.NumberFormat("uk-UA", {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount) + " грн";
}

export function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("380")) {
    return `+380 ${digits.slice(3, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 10)} ${digits.slice(10)}`;
  }
  return phone;
}
